import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { api } from "@/lib/api";

function formatTime(milliseconds) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export function chooseDisplayDurationMs({
  propDurationMs = 0,
  storedDurationMs = 0,
  measuredDurationMs = 0,
  currentMs = 0,
}) {
  const metadataDurationMs = Math.max(
    Number(propDurationMs) || 0,
    Number(storedDurationMs) || 0,
  );
  const measured = Number(measuredDurationMs) || 0;
  // Browser-decoded/playback duration is the strongest signal for MediaRecorder
  // WebM, whose container metadata can be missing or misread. Once measured,
  // keep the visible total stable and never let transient metadata probes grow it.
  if (measured > 0) {
    return Math.max(measured, Number(currentMs) || 0);
  }
  return Math.max(metadataDurationMs, Number(currentMs) || 0);
}

async function decodeDurationMs(blob) {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return 0;
  const context = new AudioContextClass();
  try {
    const buffer = await context.decodeAudioData(await blob.arrayBuffer());
    return Number.isFinite(buffer.duration) && buffer.duration > 0
      ? Math.round(buffer.duration * 1000)
      : 0;
  } catch {
    return 0;
  } finally {
    await context.close().catch(() => {});
  }
}

export default function PrivateAudioPlayer({ mediaId, durationMs, label }) {
  const audioRef = useRef(null);
  const objectUrlRef = useRef("");
  const [source, setSource] = useState("");
  const [currentMs, setCurrentMs] = useState(0);
  const [storedDurationMs, setStoredDurationMs] = useState(0);
  const [measuredDurationMs, setMeasuredDurationMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setSource(""); setCurrentMs(0); setStoredDurationMs(0); setMeasuredDurationMs(0); setPlaying(false); setError("");
    Promise.all([
      api.get(`/private-media/assets/${mediaId}`),
      api.get(`/private-media/assets/${mediaId}/content`, { responseType: "blob" }),
    ])
      .then(([assetResponse, contentResponse]) => {
        if (!active) return;
        const storedDuration = Number(assetResponse.data?.safe_metadata?.duration_ms || 0);
        if (storedDuration > 0) setStoredDurationMs(storedDuration);
        const url = URL.createObjectURL(contentResponse.data);
        objectUrlRef.current = url;
        setSource(url);
        decodeDurationMs(contentResponse.data).then((decoded) => {
          if (active && decoded > 0) setMeasuredDurationMs(decoded);
        });
      })
      .catch(() => { if (active) setError("Не удалось загрузить запись"); });
    return () => {
      active = false;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = "";
    };
  }, [mediaId]);

  const displayDurationMs = chooseDisplayDurationMs({
    propDurationMs: durationMs,
    storedDurationMs,
    measuredDurationMs,
    currentMs,
  });
  const displayedCurrentMs = displayDurationMs > 0
    ? Math.min(currentMs, displayDurationMs)
    : currentMs;

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio || !source) return;
    if (audio.paused) await audio.play();
    else audio.pause();
  };

  return <div className="flex min-w-0 items-center gap-3 rounded-xl border border-[#E3D5C0] bg-white px-3 py-2" data-testid="private-audio-player">
    <audio ref={audioRef} src={source} preload="metadata"
      onLoadedMetadata={(event) => {
        const seconds = event.currentTarget.duration;
        const hasTrustedMetadata = Number(durationMs || storedDurationMs) > 0;
        if (
          !hasTrustedMetadata &&
          measuredDurationMs <= 0 &&
          Number.isFinite(seconds) &&
          seconds > 0
        ) {
          setMeasuredDurationMs(Math.round(seconds * 1000));
        } else if (seconds === Infinity) {
          // MediaRecorder WebM commonly omits the container Duration element.
          // Seeking once lets Chromium derive it from the existing blob timeline.
          event.currentTarget.currentTime = Number.MAX_SAFE_INTEGER;
        }
      }}
      onDurationChange={(event) => {
        const seconds = event.currentTarget.duration;
        // Chromium may emit several provisional WebM durations (for example
        // 4s, then 22s, then the real 18s). decodeAudioData/onEnded provide the
        // stable measurement; do not replace it with transient durationchange.
        if (
          measuredDurationMs <= 0 &&
          Number.isFinite(seconds) &&
          seconds > 0 &&
          Number(durationMs || storedDurationMs) <= 0
        ) {
          setMeasuredDurationMs(Math.round(seconds * 1000));
        }
        if (Number.isFinite(seconds) && event.currentTarget.currentTime > seconds) {
          event.currentTarget.currentTime = 0;
        }
      }}
      onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
      onEnded={(event) => {
        const seconds = event.currentTarget.currentTime;
        if (Number.isFinite(seconds) && seconds > 0) {
          setMeasuredDurationMs(Math.round(seconds * 1000));
        }
        setPlaying(false);
        setCurrentMs(0);
      }}
      onTimeUpdate={(event) => {
        const audio = event.currentTarget;
        const positionMs = Math.round(audio.currentTime * 1000);
        if (!Number.isFinite(audio.duration) && positionMs > 0) {
          // The metadata-probe seek reached the real end of an existing WebM.
          audio.currentTime = 0;
          setCurrentMs(0);
        } else {
          setCurrentMs(positionMs);
        }
      }} />
    <button type="button" disabled={!source} onClick={toggle} aria-label={playing ? `Пауза: ${label}` : `Прослушать: ${label}`}
      className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#4A3728] text-white disabled:opacity-40">
      {playing ? <Pause size={16} /> : <Play size={16} />}
    </button>
    <div className="min-w-0 text-sm text-[#4E3B2C]">
      <div className="truncate">{label}</div>
      <div className="font-mono text-xs text-[#79634F]">{formatTime(displayedCurrentMs)} / {formatTime(displayDurationMs)}</div>
    </div>
    {error && <span className="text-xs text-[#9A3F35]" role="alert">{error}</span>}
  </div>;
}
